import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { deletePost, getPostShortList } from "../../api";
import { showLovableToast } from "../../components/common/LovableToast/showLovableToast";
import { getPublishers } from "../../flux/publisherAction";
import publisherStore from "../../flux/publisherStore";
import { getPublisherAvatarUrl } from "../../utils";
import {
  CmsActions,
  CmsAvatar,
  CmsClearBtn,
  CmsDateCell,
  CmsDeleteActions,
  CmsDeleteBackdrop,
  CmsDeleteCancel,
  CmsDeleteConfirm,
  CmsDeleteCopy,
  CmsDeleteDialog,
  CmsDeleteTitle,
  CmsEmpty,
  CmsEmptyIcon,
  CmsEmptyText,
  CmsEmptyTitle,
  CmsEyebrow,
  CmsHeroActions,
  CmsIconBtn,
  CmsIconLink,
  CmsIdCell,
  CmsLead,
  CmsList,
  CmsListCard,
  CmsListHead,
  CmsLoading,
  CmsMain,
  CmsMasthead,
  CmsOutlineBtn,
  CmsOutlineLink,
  CmsPrimaryLink,
  CmsPublisherAll,
  CmsPublisherBtn,
  CmsPublisherCell,
  CmsPublisherLabel,
  CmsPublisherRow,
  CmsRestricted,
  CmsRestrictedActions,
  CmsRestrictedIcon,
  CmsRestrictedText,
  CmsRestrictedTitle,
  CmsRoleBadge,
  CmsRow,
  CmsSearchInput,
  CmsSearchRow,
  CmsSearchWrap,
  CmsSentinel,
  CmsStatCard,
  CmsStatHint,
  CmsStatIcon,
  CmsStatLabel,
  CmsStats,
  CmsStatValue,
  CmsStatus,
  CmsStatusSeg,
  CmsStatusSegBtn,
  CmsTitle,
  CmsTitleBlock,
  CmsTitleLink,
  CmsToolbarCard,
  CmsViewsCell,
} from "./Cms.styles";

const PAGE_SIZE = 50;

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M5 12h14" />
    <path d="M12 5v14" />
  </svg>
);

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

const PencilIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18" />
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
  </svg>
);

const FileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
    <path d="M12 8v4" />
    <path d="M12 16h.01" />
  </svg>
);

const LoaderIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

const LayersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" />
    <path d="m22 12.5-8.58 3.91a2 2 0 0 1-1.66 0L2.6 12.5" />
    <path d="m22 17.5-8.58 3.91a2 2 0 0 1-1.66 0L2.6 17.5" />
  </svg>
);

const CircleDotIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="1" fill="currentColor" />
  </svg>
);

const PenLineIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const readUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user"));
  } catch {
    return null;
  }
};

const parsePostDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const normalized = String(value).replace(" ", "T");
  const d = new Date(
    normalized.endsWith("Z") ? normalized : `${normalized}Z`
  );
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatCmsDate = (value) => {
  const d = parsePostDate(value);
  if (!d) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const calendarDaysFromToday = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return Math.round((today.getTime() - day.getTime()) / 86400000);
};

const relativeLabel = (value) => {
  const d = parsePostDate(value);
  if (!d) return "";
  const days = calendarDaysFromToday(d);
  if (days < 0) {
    const ahead = Math.abs(days);
    if (ahead === 1) return "tomorrow";
    if (ahead < 7) return `in ${ahead} days`;
    if (ahead < 30) return `in ${Math.floor(ahead / 7)}w`;
    return `in ${Math.floor(ahead / 30)}mo`;
  }
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

const CmsPage = () => {
  const user = readUser();
  const isAdmin = user?.userRoleId === 1;
  const isPublisher = user?.userRoleId === 3;
  const hasAccess = !!(user && (isAdmin || isPublisher));
  const roleLabel = isAdmin ? "admin" : isPublisher ? "publisher" : "";

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [posts, setPosts] = useState([]);
  const [totalPosts, setTotalPosts] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [publishers, setPublishers] = useState(
    publisherStore.getPublishers() || []
  );
  const [filters, setFilters] = useState({ title: "", authorId: null });
  const [statusFilter, setStatusFilter] = useState("all");
  const [pendingDeletePost, setPendingDeletePost] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const listRef = useRef(null);
  const fetchingMoreRef = useRef(false);

  useEffect(() => {
    const onChange = () => setPublishers(publisherStore.getPublishers() || []);
    publisherStore.addChangeListener(onChange);
    if (publisherStore.getPublishers().length === 0) {
      getPublishers();
    }
    return () => publisherStore.removeChangeListener(onChange);
  }, []);

  const fetchPosts = useCallback(async (page = 0, nextFilters = filters) => {
    const res = await getPostShortList(PAGE_SIZE, page, nextFilters);
    return {
      items: res?.data?.items || [],
      total:
        typeof res?.data?.total === "number" ? res.data.total : null,
    };
  }, [filters]);

  const loadInitial = useMemo(
    () =>
      debounce(async (nextFilters) => {
        setLoading(true);
        setHasMore(true);
        setCurrentPage(0);
        try {
          const res = await getPostShortList(PAGE_SIZE, 0, nextFilters);
          const list = res?.data?.items || [];
          const total =
            typeof res?.data?.total === "number"
              ? res.data.total
              : list.length;
          setPosts(list);
          setTotalPosts(total);
          setHasMore(list.length < total);
        } finally {
          setLoading(false);
        }
      }, 500),
    []
  );

  useEffect(() => {
    if (!hasAccess) {
      setLoading(false);
      return undefined;
    }
    loadInitial(filters);
    return () => loadInitial.cancel();
  }, [hasAccess, filters, loadInitial]);

  const loadMore = useCallback(async () => {
    if (!hasMore || fetchingMoreRef.current || loading) return;
    fetchingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const { items, total } = await fetchPosts(nextPage, filters);
      const knownTotal = typeof total === "number" ? total : totalPosts;
      if (typeof total === "number") {
        setTotalPosts(total);
      }
      const nextLength = posts.length + items.length;
      setPosts((prev) => prev.concat(items));
      setCurrentPage(nextPage);
      setHasMore(nextLength < knownTotal);
    } finally {
      setLoadingMore(false);
      fetchingMoreRef.current = false;
    }
  }, [currentPage, fetchPosts, filters, hasMore, loading, posts.length, totalPosts]);

  useEffect(() => {
    if (!hasAccess) return undefined;
    const onScroll = () => {
      const el = listRef.current;
      if (!el) return;
      if (el.getBoundingClientRect().bottom <= window.innerHeight + 120) {
        loadMore();
      }
    };
    document.addEventListener("scroll", onScroll);
    return () => document.removeEventListener("scroll", onScroll);
  }, [hasAccess, loadMore]);

  const onSearch = (title) => {
    setFilters((prev) => ({ ...prev, title }));
  };

  const applyPublisherFilter = (publisherId) => {
    setFilters((prev) => ({
      ...prev,
      authorId: prev.authorId === publisherId ? null : publisherId,
    }));
  };

  const clearFilters = () => {
    setFilters({ title: "", authorId: null });
    setStatusFilter("all");
  };

  const confirmDeletePost = async () => {
    if (!pendingDeletePost) return;
    setDeleting(true);
    try {
      const res = await deletePost(pendingDeletePost.id);
      const status = res?.status ?? res?.response?.status;
      if (status >= 200 && status < 300) {
        setPosts((prev) => prev.filter((p) => p.id !== pendingDeletePost.id));
        setTotalPosts((prev) => Math.max(0, prev - 1));
        setPendingDeletePost(null);
        showLovableToast("Post deleted");
        return;
      }

      const apiMessage =
        res?.response?.data?.error ||
        res?.data?.error ||
        res?.message;
      showLovableToast(
        typeof apiMessage === "string" && apiMessage
          ? apiMessage
          : "Couldn't delete post. Please try again."
      );
    } catch {
      showLovableToast("Couldn't delete post. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const visiblePublishers = useMemo(
    () => (publishers || []).filter((p) => p.isHidden === false),
    [publishers]
  );

  const publisherById = useMemo(() => {
    const map = {};
    visiblePublishers.forEach((p) => {
      map[p.id] = p;
    });
    return map;
  }, [visiblePublishers]);

  const counts = useMemo(
    () => ({
      total: totalPosts,
      published: posts.filter((p) => !p.isDraft).length,
      drafts: posts.filter((p) => p.isDraft).length,
    }),
    [posts, totalPosts]
  );

  const shown = useMemo(() => {
    if (statusFilter === "Published") {
      return posts.filter((p) => !p.isDraft);
    }
    if (statusFilter === "Draft") {
      return posts.filter((p) => p.isDraft);
    }
    return posts;
  }, [posts, statusFilter]);

  const hasFilters =
    !!filters.title || !!filters.authorId || statusFilter !== "all";

  if (!hasAccess) {
    return (
      <CmsMain>
        <CmsRestricted>
          <CmsRestrictedIcon>
            <ShieldIcon />
          </CmsRestrictedIcon>
          <CmsRestrictedTitle>Content studio is restricted</CmsRestrictedTitle>
          <CmsRestrictedText>
            Only admins and publishers can manage posts. If you believe you
            should have access, contact the bitcoin.live team.
          </CmsRestrictedText>
          <CmsRestrictedActions>
            <CmsPrimaryLink to="/blogs/premium">
              Back to member content
            </CmsPrimaryLink>
            <CmsOutlineLink to="/">Back to home</CmsOutlineLink>
          </CmsRestrictedActions>
        </CmsRestricted>
      </CmsMain>
    );
  }

  return (
    <CmsMain>
      <CmsMasthead>
        <div>
          <CmsEyebrow>Content studio</CmsEyebrow>
          <CmsTitle>
            Publish the <span>signal</span>
          </CmsTitle>
          <CmsLead>
            {isAdmin
              ? "Every post across bitcoin.live, from every analyst desk."
              : "Your desk. Draft, refine, and publish your market commentary."}
          </CmsLead>
        </div>
        <CmsHeroActions>
          <CmsRoleBadge>{roleLabel}</CmsRoleBadge>
          <CmsPrimaryLink to="/add-new-post">
            <PlusIcon />
            Create new post
          </CmsPrimaryLink>
        </CmsHeroActions>
      </CmsMasthead>

      <CmsStats>
        <CmsStatCard style={{ animationDelay: "0ms" }}>
          <CmsStatIcon>
            <LayersIcon />
          </CmsStatIcon>
          <div>
            <CmsStatValue>{counts.total}</CmsStatValue>
            <CmsStatLabel>Total posts</CmsStatLabel>
          </div>
        </CmsStatCard>
        <CmsStatCard style={{ animationDelay: "70ms" }}>
          <CmsStatIcon>
            <CircleDotIcon />
          </CmsStatIcon>
          <div>
            <CmsStatValue>{counts.published}</CmsStatValue>
            <CmsStatLabel>Published</CmsStatLabel>
            <CmsStatHint>Loaded so far</CmsStatHint>
          </div>
        </CmsStatCard>
        <CmsStatCard style={{ animationDelay: "140ms" }}>
          <CmsStatIcon>
            <PenLineIcon />
          </CmsStatIcon>
          <div>
            <CmsStatValue>{counts.drafts}</CmsStatValue>
            <CmsStatLabel>Drafts</CmsStatLabel>
            <CmsStatHint>Loaded so far</CmsStatHint>
          </div>
        </CmsStatCard>
      </CmsStats>

      <CmsToolbarCard>
        <CmsSearchRow>
          <CmsSearchWrap>
            <SearchIcon />
            <CmsSearchInput
              value={filters.title}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Search posts by title…"
              aria-label="Search posts by title"
            />
          </CmsSearchWrap>

          <CmsStatusSeg>
            {["all", "Published", "Draft"].map((s) => (
              <CmsStatusSegBtn
                key={s}
                type="button"
                $active={statusFilter === s}
                aria-pressed={statusFilter === s}
                onClick={() => setStatusFilter(s)}
              >
                {s === "all" ? "All" : s}
              </CmsStatusSegBtn>
            ))}
          </CmsStatusSeg>

          {hasFilters ? (
            <CmsClearBtn type="button" onClick={clearFilters}>
              <XIcon />
              Clear
            </CmsClearBtn>
          ) : null}
        </CmsSearchRow>

        {isAdmin ? (
          <CmsPublisherRow>
            <CmsPublisherLabel>Publisher</CmsPublisherLabel>
            <CmsPublisherAll
              type="button"
              $all
              $active={!filters.authorId}
              title="All publishers"
              aria-pressed={!filters.authorId}
              onClick={() =>
                setFilters((prev) => ({ ...prev, authorId: null }))
              }
            >
              All
            </CmsPublisherAll>
            {visiblePublishers.map((publisher) => (
              <CmsPublisherBtn
                key={publisher.id}
                type="button"
                $active={filters.authorId === publisher.id}
                title={publisher.name}
                aria-label={`Filter by ${publisher.name}`}
                aria-pressed={filters.authorId === publisher.id}
                onClick={() => applyPublisherFilter(publisher.id)}
              >
                <img
                  src={getPublisherAvatarUrl(publisher)}
                  alt={publisher.name}
                />
              </CmsPublisherBtn>
            ))}
          </CmsPublisherRow>
        ) : null}
      </CmsToolbarCard>

      <CmsListCard>
        <CmsListHead $admin={isAdmin}>
          <span>#</span>
          <span>Title</span>
          {isAdmin ? <span>Publisher</span> : null}
          <span>Views</span>
          <span>Status</span>
          <span>Published</span>
          <span>Actions</span>
        </CmsListHead>

        {loading ? (
          <CmsLoading>
            <LoaderIcon />
            Loading posts…
          </CmsLoading>
        ) : shown.length === 0 ? (
          <CmsEmpty>
            <CmsEmptyIcon>
              <FileIcon />
            </CmsEmptyIcon>
            <CmsEmptyTitle>No posts found</CmsEmptyTitle>
            <CmsEmptyText>
              Try a different title search or clear your filters.
            </CmsEmptyText>
            {hasFilters ? (
              <CmsOutlineBtn type="button" onClick={clearFilters}>
                Clear filters
              </CmsOutlineBtn>
            ) : null}
          </CmsEmpty>
        ) : (
          <CmsList ref={listRef} id="posts-list">
            {shown.map((post, index) => {
              const authorPub =
                publisherById[post.author?.id] ||
                publisherById[post.authorId] ||
                null;
              const avatarSrc = authorPub
                ? getPublisherAvatarUrl(authorPub)
                : post.author?.avatar?.small || null;
              const publishedAt = post.publishedAt || post.updatedAt;
              const relative = relativeLabel(publishedAt);

              return (
                <CmsRow
                  key={post.id}
                  $admin={isAdmin}
                  style={{
                    animationDelay: `${Math.min(index % PAGE_SIZE, 11) * 35}ms`,
                  }}
                >
                  <CmsIdCell>{post.id}</CmsIdCell>
                  <CmsTitleBlock>
                    {!isAdmin && avatarSrc ? (
                      <CmsAvatar src={avatarSrc} alt="" />
                    ) : null}
                    <CmsTitleLink to={`/edit-post/${post.id}`}>
                      {post.title}
                    </CmsTitleLink>
                  </CmsTitleBlock>

                  {isAdmin ? (
                    <CmsPublisherCell>
                      {avatarSrc ? <img src={avatarSrc} alt="" /> : null}
                      <span>{post.author?.name || "—"}</span>
                    </CmsPublisherCell>
                  ) : null}

                  <CmsViewsCell>
                    <span className="mdOnly">Views </span>
                    {post.numViews ? post.numViews : "—"}
                  </CmsViewsCell>

                  <div>
                    <CmsStatus $published={!post.isDraft}>
                      {post.isDraft ? "Draft" : "Published"}
                    </CmsStatus>
                  </div>

                  <CmsDateCell>
                    <span className="mdOnly">Published </span>
                    {formatCmsDate(publishedAt)}
                    {relative ? <em> · {relative}</em> : null}
                  </CmsDateCell>

                  <CmsActions>
                    <CmsIconLink
                      to={`/edit-post/${post.id}`}
                      aria-label={`Edit ${post.title}`}
                    >
                      <PencilIcon />
                    </CmsIconLink>
                    {(isAdmin || post.isDraft) && (
                      <CmsIconBtn
                        type="button"
                        aria-label={`Delete ${post.title}`}
                        onClick={() =>
                          setPendingDeletePost({
                            id: post.id,
                            title: post.title,
                          })
                        }
                      >
                        <TrashIcon />
                      </CmsIconBtn>
                    )}
                  </CmsActions>
                </CmsRow>
              );
            })}
          </CmsList>
        )}
      </CmsListCard>

      <CmsSentinel>
        {loadingMore ? (
          <>
            <LoaderIcon />
            Loading more posts…
          </>
        ) : null}
      </CmsSentinel>

      {pendingDeletePost ? (
        <CmsDeleteBackdrop
          role="presentation"
          onClick={() => {
            if (!deleting) setPendingDeletePost(null);
          }}
        >
          <CmsDeleteDialog
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="cms-delete-title"
            aria-describedby="cms-delete-desc"
            onClick={(e) => e.stopPropagation()}
          >
            <CmsDeleteTitle id="cms-delete-title">
              Delete this post?
            </CmsDeleteTitle>
            <CmsDeleteCopy id="cms-delete-desc">
              “{pendingDeletePost.title}” will be removed permanently. This
              can&apos;t be undone.
            </CmsDeleteCopy>
            <CmsDeleteActions>
              <CmsDeleteCancel
                type="button"
                disabled={deleting}
                onClick={() => setPendingDeletePost(null)}
              >
                Cancel
              </CmsDeleteCancel>
              <CmsDeleteConfirm
                type="button"
                disabled={deleting}
                onClick={confirmDeletePost}
              >
                {deleting ? "Deleting…" : "Delete post"}
              </CmsDeleteConfirm>
            </CmsDeleteActions>
          </CmsDeleteDialog>
        </CmsDeleteBackdrop>
      ) : null}
    </CmsMain>
  );
};

export default CmsPage;
